import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const DarkModeContext = createContext(null);

export const useDarkMode = () => {
  const ctx = useContext(DarkModeContext);
  if (!ctx) throw new Error('useDarkMode must be used within a DarkModeProvider');
  return ctx;
};

export const DarkModeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false; // SSR-safe default
    try {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    } catch {
      return false;
    }
  });

  // keep <html> class + storage + meta theme-color in sync
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', isDarkMode);
    try { localStorage.setItem('darkMode', JSON.stringify(isDarkMode)); } catch {}

    // Optional: helps mobile browser chrome match theme
    const meta = document.querySelector('meta[name="theme-color"]') || (() => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'theme-color');
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute('content', isDarkMode ? '#0f0f0f' : '#ffffff');
  }, [isDarkMode]);

  // react to OS-level changes
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const saved = localStorage.getItem('darkMode');
      if (saved === null) setIsDarkMode(e.matches); // only auto-switch if user hasn't explicitly chosen
    };
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  const toggleDarkMode = () => setIsDarkMode((v) => !v);

  const value = useMemo(() => ({ isDarkMode, toggleDarkMode }), [isDarkMode]);
  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>;
};
